import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ArrowRight, Eye, EyeOff, Lock, Mail, ShieldCheck } from "lucide-react";

import BrandLogo from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { CaptchaField, useCaptcha } from "@/components/Captcha";

import { isSuperAdminSignedIn, superAdminLogin } from "./session";

export default function SuperAdminLoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const captcha = useCaptcha();

  if (isSuperAdminSignedIn()) return <Navigate to="/super-admin/home" replace />;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      await superAdminLogin(email, password, captcha.payload());
      navigate("/super-admin/home", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid super admin credentials.");
      // Challenge tokens are single-use — a failed attempt needs a fresh one,
      // otherwise the next submit fails on the captcha rather than the password.
      captcha.refresh();
    } finally {
      setLoading(false);
    }
  };

  // Pinned to Dark · Gold, like the admin sign-in screen.
  return (
    <div data-theme="dark" className="public-stage flex min-h-screen w-full flex-col justify-center px-5 py-10 sm:px-8">
      <div className="mx-auto w-full max-w-[400px]">
        <BrandLogo variant="dark" className="h-12 w-auto object-contain" />

        <div className="mt-8 flex items-center justify-between gap-3 border-b border-border/60">
          <span className="relative -mb-px flex items-center gap-2 pb-3 text-sm font-semibold text-primary">
            <ShieldCheck className="h-4 w-4" />
            Super Admin Sign In
            <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-primary" />
          </span>
          <span className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-[0.6rem] font-bold uppercase tracking-[0.14em] text-primary">
            <Lock className="h-3.5 w-3.5" />
            Restricted
          </span>
        </div>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="sa-email">Email Address</Label>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
              <Input
                id="sa-email"
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(event) => { setEmail(event.target.value); setError(""); }}
                className="pl-10"
                required
                autoComplete="username"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="sa-password">Password</Label>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
              <Input
                id="sa-password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={(event) => { setPassword(event.target.value); setError(""); }}
                className="pl-10 pr-11"
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((value) => !value)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <CaptchaField captcha={captcha} />

          {error && <p className="text-xs font-medium text-destructive">{error}</p>}

          <Button type="submit" size="lg" className="w-full" disabled={loading || captcha.incomplete}>
            {loading ? "Authenticating..." : "Sign In"}
            {!loading && <ArrowRight className="h-4 w-4" />}
          </Button>
        </form>
      </div>
    </div>
  );
}
